import * as React from 'react';

import cookie from 'js/lib/cookie';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import type {
  AIMessageDraft,
  AudienceDelta,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { compileMessageCml } from 'bundles/enterprise-admin-messaging-v2/utils/CMLUtils';

// Mirrors the legacy enterprise-admin-messages send (see EmailPreviewFrame):
// a TIME-triggered "enterpriseLearnerCommunicationMessage" draft, scoped to the
// org, whose audience is resolved server-side from the saved filterId.
const TRIGGER_TYPE = 'TIME';
const TRIGGER_NAME = 'enterpriseLearnerCommunicationTrigger';
const MESSAGE_TYPE_NAME = 'enterpriseLearnerCommunicationMessage';

const DRAFTS_URL = '/api/triggeredMessageDrafts.v1';
const TRIGGERED_MESSAGES_URL = '/api/enterpriseTriggeredMessages.v1';

// `CallToActionId.DO_NOT_INCLUDE` from enterprise-admin-messages — the "no CTA
// button" option. Inlined to avoid a cross-bundle dependency for one constant.
// The draft API requires `callToAction`, so messages with no CTA send this.
export const NO_CALL_TO_ACTION = 'DO_NOT_INCLUDE';

type SendArgs = {
  draft: AIMessageDraft;
  orgId: string;
  /** Saved AdminUserFilter id the audience resolves from (queryType ADMIN_USER_FILTER). */
  filterId: string;
  /** Legacy PresetId for the message: 'remind-join' | 'remind-enroll' | 'custom' | … */
  preset: string;
  /**
   * CTA preset id (required by the draft API). Use a real CTA (e.g. JOIN_PROGRAM)
   * for the funnel reminders, or `DO_NOT_INCLUDE` for messages with no CTA button
   * (the learners-table custom send).
   */
  callToAction: string;
  /**
   * Programs the message is scoped to, carried in `filterParams.programIds`. The
   * `JOIN_PROGRAM` CTA resolves its button link from this program context, so
   * program-scoped reminders must pass the triggering program here or the link
   * renders empty (`/programs//`). Omit for org-wide / non-program audiences.
   */
  programIds?: string[];
  /**
   * Learner add/remove delta from the Edit learner list screen. When present,
   * the draft carries `enterpriseCohortContext.cohortOverride` so the BE can
   * resolve the final recipient list from the base filter ± overrides.
   */
  audienceDelta?: AudienceDelta;
};

/**
 * Builds the `triggeredMessageDrafts.v1` create payload from our draft, mapping
 * onto the EXISTING send contract:
 *  - audience → the saved `filterId` (queryType ADMIN_USER_FILTER)
 *  - copy → compiled CML (the only content type the API accepts today; see TODO)
 *  - preset → the entry point's legacy PresetId
 *  - CTA → the given preset + the AI CTA label as the text override, when present
 *  - sender identity → the signed-in admin
 *  - cohort override → when the admin edits the learner list, `enterpriseCohortContext`
 *    carries the base filter id + added/removed learners so the BE resolves the
 *    final recipient list from the override delta.
 */
export const buildCreateDraftPayload = ({
  draft,
  orgId,
  filterId,
  preset,
  callToAction,
  programIds,
  audienceDelta,
}: SendArgs) => {
  const hasCohortOverride = audienceDelta && (audienceDelta.removedIds.length > 0 || audienceDelta.addedIds.length > 0);

  return {
    triggerType: TRIGGER_TYPE,
    triggerName: TRIGGER_NAME,
    scope: tupleToStringKey(['ThirdPartyOrganization', orgId]),
    draft: {
      message: {
        typeName: MESSAGE_TYPE_NAME,
        definition: {
          subject: draft.subject,
          content: compileMessageCml(draft.message),
          preset,
          queryType: 'ADMIN_USER_FILTER',
          filterParams: { filterId, orgId, ...(programIds?.length ? { programIds } : {}) },
          callToAction,
          ...(callToAction !== NO_CALL_TO_ACTION ? { callToActionTextOverride: draft.ctaText } : {}),
          senderName: user.get().full_name,
          authorUserId: user.get().id,
          replyTo: user.get().email_address,
          ...(hasCohortOverride && {
            enterpriseCohortContext: {
              orgId,
              prompt: draft.prompt,
              sourcePayload: {},
              cohortOverride: {
                queryOrFilterId: filterId,
                learnersAdded: audienceDelta.addedIds,
                learnersRemoved: audienceDelta.removedIds,
              },
            },
          }),
        },
      },
      metadata: { version: 1, triggerSchedule: [] },
    },
  };
};

export type SendMessageResult = { status: 'success' } | { status: 'error'; error: string };

export type PreviewMessageResult = { status: 'success'; html: string } | { status: 'error'; error: string };

// Naptime POSTs require the CSRF token header; `executeMutation` adds it
// automatically, but a raw fetch must attach it explicitly.
const postJson = async (url: string, body: unknown): Promise<Response> =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF3-Token': cookie.get('CSRF3-Token') ?? '',
    },
    body: JSON.stringify(body),
  });

/**
 * Sends the message through the existing triggered-messages pipeline:
 * create draft → publish → send, mirroring the legacy `EmailPreviewFrame`.
 * Requires `draft.filterId` (created on Review); returns a discriminated result
 * and exposes `isSending` for the Send button's loading state.
 *
 * TODO: verify the exact request/response envelope against the BE once the new
 * HTML-template payload is live (we currently parse the naptime `elements[0].id`
 * shape used by the legacy flow).
 */
export const useSendMessage = () => {
  const [isSending, setIsSending] = React.useState(false);

  // Render the email exactly as the backend will send it, by creating a throwaway
  // draft and asking the API to render its HTML (mirrors the legacy
  // EmailPreviewFrame `saveDraft` → `action=preview`). This is intentionally a
  // SEPARATE draft from the one the actual send creates: the admin can edit the
  // copy after previewing, so `sendMessage` re-creates the draft from the latest
  // state on the Send click rather than publishing this preview draft.
  //
  // TODO: confirm the preview response envelope against the BE (we parse the
  // legacy `{ body }` HTML shape, same as `EmailPreviewFrame.loadPreview`).
  const previewMessage = React.useCallback(async (args: SendArgs): Promise<PreviewMessageResult> => {
    if (!args.filterId) {
      return { status: 'error', error: 'Audience filter is not ready yet.' };
    }
    try {
      const payload = buildCreateDraftPayload(args);
      const createResponse = await postJson(DRAFTS_URL, payload);
      if (!createResponse.ok) throw new Error('Could not create the message draft.');
      const created = await createResponse.json();
      const messageId: string | undefined = created?.elements?.[0]?.id ?? created?.data?.elements?.[0]?.id;
      if (!messageId) throw new Error('Could not create the message draft.');

      const id = encodeURIComponent(messageId);
      // The preview action's body is just the message object (not the full create
      // envelope), matching the legacy `loadPreview` call.
      const previewResponse = await postJson(`${DRAFTS_URL}?action=preview&id=${id}`, payload.draft.message);
      if (!previewResponse.ok) throw new Error('Could not load the preview.');
      const previewData = await previewResponse.json();
      const html: string | undefined = previewData?.body ?? previewData?.data?.body;
      if (typeof html !== 'string') throw new Error('Could not load the preview.');

      return { status: 'success', html };
    } catch (e) {
      return { status: 'error', error: e instanceof Error ? e.message : 'Failed to load the preview.' };
    }
  }, []);

  const sendMessage = React.useCallback(async (args: SendArgs): Promise<SendMessageResult> => {
    if (!args.filterId) {
      return { status: 'error', error: 'Audience filter is not ready yet.' };
    }
    setIsSending(true);
    try {
      const createResponse = await postJson(DRAFTS_URL, buildCreateDraftPayload(args));
      if (!createResponse.ok) throw new Error('Could not create the message draft.');
      const created = await createResponse.json();
      const messageId: string | undefined = created?.elements?.[0]?.id ?? created?.data?.elements?.[0]?.id;
      if (!messageId) throw new Error('Could not create the message draft.');

      const id = encodeURIComponent(messageId);
      const publishResponse = await postJson(`${DRAFTS_URL}?action=publish&id=${id}`, { version: 2 });
      if (!publishResponse.ok) throw new Error('Could not publish the message.');

      const sendResponse = await postJson(`${TRIGGERED_MESSAGES_URL}?action=send&id=${id}&dryMode=false`, {});
      if (!sendResponse.ok) throw new Error('Could not send the message.');

      return { status: 'success' };
    } catch (e) {
      return { status: 'error', error: e instanceof Error ? e.message : 'Failed to send the message.' };
    } finally {
      setIsSending(false);
    }
  }, []);

  return { sendMessage, previewMessage, isSending };
};

export default useSendMessage;
